import React, { useState } from 'react';
import { AssignedCandidate } from '../../models/assigned-candidate.interface';
import { useTranslation } from 'react-i18next';
import Pagination from '../pagination/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
    calculateCandidateMatch,
    getAssignedCandidates,
    setCandidateStatus,
} from '../../store/slices/companyCandidatesSlice';
import CandidatesTableMobile from './CandidatesTableMobile';
import {
    setClickedMatchPayload,
    setIsLimitReachedModal,
    setIsMatchWarningModal,
} from '../../store/slices/modalSlice';
import { plans } from '../../constants/constants';
import { Image } from 'react-bootstrap';
import ActionBar from '../ui/action-bar/ActionBar';
import { ASSETS_URL } from '../../constants';
import MatchSubmitModal from '../ui/match-submit-modal/MatchSubmitModal';
import { MatchPayload } from '../../models/match-payload';
import { TableColumn } from '../../models/table-column.interface';
import CandidateTableHead from './CandidateTableHead';
import CandidateTableBody from './CandidateTableBody';
import { checkIfMatchLimitReached } from '../../utils/check-if-match-limit-reached';
import { increaseLimitCounter } from '../../store/slices/companySlice';
import { Modal, Button, Form } from 'react-bootstrap';
import ApiService from '../../core/services/network.service';
import * as endpoints from '../../constants/endpoints';

interface TableProps {
    candidates: AssignedCandidate[];
    columns: TableColumn[];
    handleRowCheck: (id: string) => void;
    selectAll: boolean;
    pageNumber: number;
    setPageNumberHandler: (page: number) => void;
    uncheckAll: () => void;
    checkAll: () => void;
    searchValue: string;
    maxAmount: number;
    setAmount: (value: number) => void;
    statusValue: string;
}

const CandidatesTable = ({
                             candidates,
                             columns,
                             handleRowCheck,
                             selectAll,
                             pageNumber,
                             setPageNumberHandler,
                             uncheckAll,
                             checkAll,
                             searchValue,
                             maxAmount,
                             setAmount,
                             statusValue,
                         }: TableProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();
    const company = useSelector((state: RootState) => state.companyReducer);
    const [selectedMatchIds, setSelectedMatchIds] = useState<string[]>([]);
    const total = useSelector(
        (state: RootState) => state.companyCandidatesReducer.total
    );

    const barActions = [
        {
            action: () => {
                matchMultipleCandidates();
                uncheckAll();
            },
            image: null,
            text: t('candidatesTablet.actionBar.match'),
        },
        {
            action: uncheckAll,
            image: null,
            text: t('vacanciesTablet.actionBar.cancel'),
        },
        {
            action: openBulkCopy,
            image: null,
            text: t('candidatesTablet.actionBar.copy'),
        },
    ];

    // Bulk copy modal state
    const [showCopy, setShowCopy] = useState(false);
    const [offers, setOffers] = useState<Array<{ id: number; position: string }>>([]);
    const [targetOfferId, setTargetOfferId] = useState<string>('');
    const [copying, setCopying] = useState(false);
    const [resultMsg, setResultMsg] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [showCopyConfirm, setShowCopyConfirm] = useState(false);

    function matchMultipleCandidates(): void {
        if (
            checkIfMatchLimitReached(
                company.request_counter?.gpt_4_requests,
                company.plan,
                company.request_counter?.total_requests
            )
        ) {
            dispatch(setIsLimitReachedModal(true));
            return;
        }

        const { matchArr, selectedMatchIds } = getSelectedForMatch();

        // @ts-ignore
        if (company.plan?.name === plans.premium.name) {
            dispatch(increaseLimitCounter(selectedMatchIds.length));
            dispatch(calculateCandidateMatch({ matchArr, selectedMatchIds }));
            return;
        }

        dispatch(setClickedMatchPayload(matchArr));
        setSelectedMatchIds(selectedMatchIds);
        dispatch(setIsMatchWarningModal(true));
    }

    const getSelectedForMatch = () => {
        const matchArr: MatchPayload[] = [];
        const selectedMatchIds: string[] = [];

        candidates
            .filter((item) => item.checked)
            .forEach((candidate: AssignedCandidate) => {
                const offerId = candidate.offer?.id;
                const candidateId = candidate.candidate.id;

                if (!offerId || !candidateId) return;

                matchArr.push({
                    offer_id: offerId,
                    candidate_id: candidateId,
                });

                selectedMatchIds.push(candidate.id || '');
            });

        return { matchArr, selectedMatchIds };
    };

    const selectedRows = candidates.filter((c) => c.checked);

    function handleStatusChange(event: React.ChangeEvent<HTMLSelectElement>): void {
        const status = +event.target.value;
        const ids = event.target.id.split('|');
        dispatch(
            setCandidateStatus({ offer_id: ids[0], candidate_id: ids[1], status: status })
        );
    }

    function onPageChange(page: number): void {
        dispatch(
            getAssignedCandidates({
                pageNumber: page,
                searchValue,
                size: maxAmount,
                statusValue,
            })
        );
        setPageNumberHandler(page);
    }

    type CopyResult = {
        ok: boolean;
        data?: any;
        row: any;
        reason?: string;
    };

    function openBulkCopy(): void {
        setResultMsg(null);
        setErrorMsg(null);
        setTargetOfferId('');
        setShowCopy(true);

        const selectedCandidateIds = [
            ...new Set(selectedRows.map(r => r.candidate.id).filter(Boolean)),
        ];
        const params = new URLSearchParams();
        selectedCandidateIds.forEach(id => {
            params.append("exclude_applied_by", id);
        });
        const queryString = params.toString();
        const url = `${endpoints.OFFERS_TITLE}${queryString ? `?${queryString}` : ''}`;
        ApiService.request<any>('GET', url)
            .then((res) => {
                const list = res.body?.results || res.body || [];
                setOffers(list);
            })
            .catch(() => setOffers([]));
    }

    async function runBulkCopy(): Promise<void> {
        if (!targetOfferId || selectedRows.length === 0) return;

        setCopying(true);
        setResultMsg(null);
        setErrorMsg(null);

        const tasks: Promise<CopyResult>[] = selectedRows.map((row) => {
            const offerId = String(row.offer?.id || '');
            const candidateId = String(row.candidate.id);
            const url = endpoints.APPLY_OFFER_COPY
                .replace('{offer_id}', offerId)
                .replace('{candidate_id}', candidateId);

            return ApiService.request<any>('POST', url, {
                target_offer_id: targetOfferId,
            })
                .then((res) => ({ ok: true, data: res.body, row } as CopyResult))
                .catch((e: any) => ({
                    ok: false,
                    row,
                    reason: e?.response?.data?.detail || 'Unknown error',
                } as CopyResult));
        });

        const results = await Promise.all(tasks);
        const ok = results.filter((r) => r.ok);
        const fail = results.filter((r) => !r.ok);

        setResultMsg(
            t('candidatesTablet.copyModal.results.success', { ok: ok.length, fail: fail.length })
        );

        if (fail.length > 0) {
            const first3 = fail
                .slice(0, 3)
                .map((f) => `${f.row.candidate.id}: ${f.reason}`)
                .join(', ');
            setErrorMsg(
                fail.length > 3
                    ? t('candidatesTablet.copyModal.results.error', { first3 })
                    : first3
            );
        }

        setCopying(false);

        if (ok.length > 0) {
            uncheckAll();
            dispatch(
                getAssignedCandidates({
                    pageNumber,
                    searchValue,
                    size: maxAmount,
                    statusValue,
                })
            );
            setTimeout(() => {
                setShowCopy(false);
            }, 500);
        }
    }

    return (
        <>
            <div className='position-relative w-100'>
                <table className='app-table mt-4 tablet-hidden' cellSpacing={5}>
                    <CandidateTableHead {...{ checkAll, uncheckAll, columns, selectAll }} />
                    <CandidateTableBody {...{ candidates, handleRowCheck, handleStatusChange }} />
                </table>

                <div className='no-tablet-hidden'>
                    <CandidatesTableMobile candidates={candidates} />
                </div>

                {candidates.filter((item) => item.checked).length ? (
                    <ActionBar selectedCount={candidates.filter((item) => item.checked).length || 0}>
                        {barActions.map((item) => (
                            <button onClick={item.action} key={item.text}>
                                {item.image && <Image src={ASSETS_URL + item.image} />}
                                {item.text}
                            </button>
                        ))}
                    </ActionBar>
                ) : null}
            </div>

            <MatchSubmitModal
                handleClose={() => dispatch(setIsMatchWarningModal(false))}
                selectedMatchIds={selectedMatchIds}
            />

            {/* Bulk copy modal */}
            <Modal show={showCopy && !showCopyConfirm} onHide={() => setShowCopy(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('candidatesTablet.copyModal.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className='mb-2'>
                        {t('candidatesTablet.copyModal.selectedCount', { count: selectedRows.length })}
                    </p>

                    <Form.Group className='mb-3'>
                        <Form.Label>{t('candidatesTablet.copyModal.destinationLabel')}</Form.Label>
                        <Form.Select
                            value={targetOfferId}
                            onChange={(e) => setTargetOfferId(e.target.value)}
                        >
                            <option value=''>
                                {t('candidatesTablet.copyModal.destinationPlaceholder')}
                            </option>
                            {offers.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {o.position}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {resultMsg && <div className='text-success'>{resultMsg}</div>}
                    {errorMsg && <div className='text-danger'>{errorMsg}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => setShowCopy(false)}>
                        {t('candidatesTablet.copyModal.buttons.close')}
                    </Button>
                    <Button
                        variant='primary'
                        onClick={() => setShowCopyConfirm(true)}
                        disabled={!targetOfferId || copying}
                    >
                        {t('candidatesTablet.copyModal.buttons.copy')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation modal before consuming match credits */}
            <Modal show={showCopyConfirm} onHide={() => setShowCopyConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('candidatesTablet.copyConfirm.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {t('candidatesTablet.copyConfirm.message', {
                            count: selectedRows.length,
                        })}
                    </p>
                    <p className='text-warning'>
                        {t('candidatesTablet.copyConfirm.warning')}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => setShowCopyConfirm(false)}>
                        {t('candidatesTablet.copyConfirm.buttons.cancel')}
                    </Button>
                    <Button
                        variant='primary'
                        onClick={() => {
                            setShowCopyConfirm(false);
                            runBulkCopy();
                        }}
                    >
                        {t('candidatesTablet.copyConfirm.buttons.confirm')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Pagination
                totalPages={Math.ceil(total / maxAmount) || 1}
                currentPage={pageNumber}
                pageSize={maxAmount}
                withAmountSelection={true}
                setAmount={setAmount}
                changeCurrentPage={onPageChange}
            />
        </>
    );
};

export default CandidatesTable;
